/**
 * ImageGrid Component
 * 
 * Displays images in a message with:
 * - Single image: maintains aspect ratio, max 300px height
 * - Multiple images: grid layout (2, 3, 4+ images)
 * - Click to view full-size
 * - Lazy loading
 */

import React from 'react';

export default function ImageGrid({ images, onImageClick }) {
  if (!images || images.length === 0) return null;

  const imageCount = images.length;

  // Single image - display with maintained aspect ratio
  if (imageCount === 1) {
    return (
      <div className="max-w-full">
        <img
          src={images[0].url}
          alt="Message attachment"
          className="rounded-lg max-h-[300px] w-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => onImageClick && onImageClick(0)}
          loading="lazy"
        />
      </div>
    );
  }

  // 2 images - side by side
  if (imageCount === 2) {
    return (
      <div className="grid grid-cols-2 gap-1 max-w-[280px]">
        {images.map((img, idx) => (
          <img
            key={idx}
            src={img.url}
            alt={`Attachment ${idx + 1}`}
            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => onImageClick && onImageClick(idx)}
            loading="lazy"
          />
        ))}
      </div>
    );
  }

  // 3 images - one large, two small
  if (imageCount === 3) {
    return (
      <div className="grid grid-cols-2 gap-1 max-w-[280px]">
        <img
          src={images[0].url}
          alt="Attachment 1"
          className="col-span-2 w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => onImageClick && onImageClick(0)}
          loading="lazy"
        />
        {images.slice(1).map((img, idx) => (
          <img
            key={idx + 1}
            src={img.url}
            alt={`Attachment ${idx + 2}`}
            className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => onImageClick && onImageClick(idx + 1)}
            loading="lazy"
          />
        ))}
      </div>
    );
  }

  // 4+ images - 2x2 grid with "+N" overlay on last if more than 4
  return (
    <div className="grid grid-cols-2 gap-1 max-w-[280px]">
      {images.slice(0, 4).map((img, idx) => (
        <div key={idx} className="relative">
          <img
            src={img.url}
            alt={`Attachment ${idx + 1}`}
            className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => onImageClick && onImageClick(idx)}
            loading="lazy"
          />
          {/* Show "+N" overlay on the 4th image if there are more */}
          {idx === 3 && imageCount > 4 && (
            <div 
              className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center cursor-pointer"
              onClick={() => onImageClick && onImageClick(idx)}
            >
              <span className="text-white text-xl font-semibold">
                +{imageCount - 4}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
